import { create } from 'zustand'
import starsWithdrawApi from '../services/starsWithdrawApi'
import useAppStore from '../../../store/useAppStore'

/**
 * Stars Withdraw Store
 * Состояние вывода кристаллов в Telegram Stars.
 * Пока есть активная заявка (new / approved), экран опрашивается раз в 30 секунд;
 * когда заявка завершилась — обновляется баланс (при отказе кристаллы вернулись).
 */

const POLLING_INTERVAL = 30000 // 30 секунд

// Коды 403, при которых раздел игроку недоступен: убираем метод из списка
const HIDE_METHOD_ERRORS = ['disabled', 'deposit required']

const hideStarsMethod = () => {
  const { user, setUser } = useAppStore.getState()
  if (!user || !('stars_withdraw_enabled' in user)) return
  const nextUser = { ...user }
  delete nextUser.stars_withdraw_enabled
  setUser(nextUser)
}

const useStarsWithdrawStore = create((set, get) => ({
  // Данные экрана из GET /api/stars-withdraw/ (котировка, лимиты, reason, active, default_username)
  info: null,
  history: [],

  isLoading: false,
  isSubmitting: false,
  error: null,

  _pollingTimer: null,

  /**
   * Применить ответ GET: сохранить данные, управлять опросом, обновить баланс по завершении заявки
   */
  _applyData: (data) => {
    const hadActive = Boolean(get().info?.active)
    set({ info: data, history: data.history || [] })

    if (data.active) {
      if (!get()._pollingTimer) get().startPolling()
    } else {
      get().stopPolling()
      if (hadActive) useAppStore.getState().loadProfile?.()
    }
  },

  _handleForbidden: (error) => {
    if (HIDE_METHOD_ERRORS.includes(error?.message)) hideStarsMethod()
  },

  /**
   * Загрузить данные экрана
   */
  loadData: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await starsWithdrawApi.fetchStarsWithdrawData()
      get()._applyData(data)
      set({ isLoading: false })
      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки данных вывода Stars:', error)
      get()._handleForbidden(error)
      set({ isLoading: false, error: error.message || 'error backend' })
      throw error
    }
  },

  /**
   * Создать заявку
   * При 409 price changed котировка в info заменяется на новую — компонент
   * показывает новую сумму и ждёт явного подтверждения
   * @param {Object} params — { stars, username, crystals }
   * @returns {Promise<Object>} созданная заявка
   */
  submitStarsWithdraw: async ({ stars, username, crystals }) => {
    set({ isSubmitting: true, error: null })
    try {
      const result = await starsWithdrawApi.createStarsWithdraw({ stars, username, crystals })
      set({ isSubmitting: false })

      try {
        await get().loadData()
      } catch {
        // GET не удался — показываем созданную заявку как активную до следующего опроса
        set((state) => ({
          info: state.info ? { ...state.info, active: result } : null,
          history: [result, ...state.history],
        }))
        get().startPolling()
      }

      return result
    } catch (error) {
      console.error('❌ Ошибка создания заявки вывода Stars:', error)
      set({ isSubmitting: false, error: error.message || 'error backend' })

      if (error.message === 'price changed') {
        const quote = error.data?.quote
        set((state) => ({
          info: quote
            ? { ...state.info, ...quote }
            : { ...state.info, price_micro: null, can_withdraw: false, reason: 'service unavailable' },
        }))
      } else if (error.message === 'active request') {
        get().loadData().catch(() => {})
      } else {
        get()._handleForbidden(error)
      }

      throw error
    }
  },

  /**
   * Опрос экрана, пока есть активная заявка
   */
  startPolling: () => {
    get().stopPolling()
    const timer = setInterval(async () => {
      try {
        const data = await starsWithdrawApi.fetchStarsWithdrawData()
        get()._applyData(data)
      } catch (error) {
        console.error('❌ Ошибка опроса вывода Stars:', error)
        get()._handleForbidden(error)
        if (HIDE_METHOD_ERRORS.includes(error?.message)) get().stopPolling()
      }
    }, POLLING_INTERVAL)
    set({ _pollingTimer: timer })
  },

  stopPolling: () => {
    const { _pollingTimer } = get()
    if (_pollingTimer) {
      clearInterval(_pollingTimer)
      set({ _pollingTimer: null })
    }
  },

  reset: () => {
    get().stopPolling()
    set({ info: null, history: [], isLoading: false, isSubmitting: false, error: null })
  },
}))

export default useStarsWithdrawStore
